/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputLabel, OutlinedInput, styled, Switch, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { countries, CurrencyJSON, FundingSource, projectStatus, projectType, ProjectUnit, TaxableOption } from '../../../common/const';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../../../redux/Actions/userActions';
import { Close } from '@mui/icons-material';
import { apiRequest } from '../../../common/common';
import { showNotification } from '../../../common/Notification/ToastifyNotification';
import { LoadingPage } from '../../../common/LoadingPage';
import moment from 'moment';
import { ClearIcon } from '@mui/x-date-pickers';
import { updateAddress } from '../../../redux/Actions/orgAction';

const DividerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 1),
    minHeight: 50
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default function CreateProject({
    projectId,
    handleClose
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, users } = useSelector(state => state.users);
    const [selectedTab, setSelectedTab] = useState(0);
    const [logo, setLogo] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [project, setProject] = useState({
        name: null,
        projectNo: null,
        clientOrg: null,
        owner: null,
        targetOccupancy: null,
        status: null,
        type: null,
        isNotes: false,
        isProjectLock: false,
        notes: null,
        fundingSource: null,
        totalCost: null,
        totalBudget: null,
        projectArea: null,
        currency: CurrencyJSON[0]?.value,
        projectUnit: ProjectUnit[0]?.value,
        isTax: false,
        taxNo: null,
        taxRate: 0,
        taxableOption: [],
        address1: null,
        address2: null,
        city: null,
        state: null,
        country: null,
        zipcode: null,
        isCertificateNeed: false,
        certificateDeadline: new Date().toISOString().split('T')[0],
        isProgramingEnable: false,
        programingDeadline: new Date().toISOString().split('T')[0],
        isSchematicDesign: false,
        schematicDesignDeadline: new Date().toISOString().split('T')[0],
        isDesignDevelopment: false,
        designDevelopmentDeadline: new Date().toISOString().split('T')[0],
        isConstructionDocument: false,
        constructionDocumentDeadline: new Date().toISOString().split('T')[0],
        isContractAdmin: false,
        contractAdminDeadline: new Date().toISOString().split('T')[0],
    });
    const [addressId, setAddressId] = useState(null);
    const [errors, setErrors] = useState({});
    const contentRef = useRef(null);

    const handleKeyDown = (event) => {
        if (!contentRef.current) return;

        const { key } = event;
        if (key === 'ArrowDown') {
            contentRef.current.scrollBy({
                top: 100,
                behavior: 'smooth',
            });
        } else if (key === 'ArrowUp') {
            contentRef.current.scrollBy({
                top: -100,
                behavior: 'smooth',
            });
        }
    };


    const handleMouseWheel = (event) => {
        if (!contentRef.current) return;

        // Check the delta of the mouse wheel movement
        const scrollAmount = event.deltaY;

        // Smoothly scroll based on the wheel direction
        contentRef.current.scrollBy({
            top: scrollAmount,
            behavior: 'smooth',
        });

        // Prevent the default behavior to avoid the page scrolling
        event.preventDefault();
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('wheel', handleMouseWheel, { passive: false });  // Add wheel event listener for mouse scroll

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('wheel', handleMouseWheel);
        };
    }, []);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        const section = document.getElementById(`section${newValue + 1}`);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (user?.organization?.organization_id) {
            dispatch(getUsers(user?.organization?.organization_id));
        }
    }, [user]);

    const validateProject = (project) => {
        const newErrors = {};

        const validationRules = [
            { field: 'name', message: 'Project Name is required' },
            { field: 'projectNo', message: 'Project ID is required' },
            { field: 'owner', message: 'Please select project owner' },
            { field: 'targetOccupancy', message: 'Please select Target Occupancy' },
            { field: 'status', message: 'Please select status of project' },
            { field: 'type', message: 'Please select project type' },
            { field: 'fundingSource', message: 'Please select source of funding' },
            { field: 'address1', message: 'Address1 is required' },
            { field: 'city', message: 'City is required' },
            { field: 'country', message: 'Please select a country' },
            { field: 'zipcode', message: 'Zip Code is required' }
        ];

        validationRules.forEach(({ field, message }) => {
            if (!project?.[field]) {
                newErrors[field] = message;
            }
        });

        return newErrors;
    };

    const handleSubmit = async () => {
        const newErrors = validateProject(project);
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            const formData = new FormData();
            const appendIfValid = (key, value) => {
                if (value !== null && value !== 'Invalid date' && value !== undefined) {
                    formData.append(key, value);
                }
            };

            appendIfValid('name', project?.name);
            appendIfValid('project_no', project?.projectNo);
            appendIfValid('client_organization', project?.clientOrg);
            appendIfValid('organization_id', user?.organization?.organization_id);
            appendIfValid('target_occupation', project?.targetOccupancy);
            appendIfValid('status', project?.status);
            appendIfValid('type', project?.type);
            appendIfValid('is_certificate_need', project?.isCertificateNeed);
            appendIfValid('certificate_deadline', project?.certificateDeadline !== 'Invalid date' ? project?.certificateDeadline : null);
            appendIfValid('is_programing_enable', project?.isProgramingEnable);
            appendIfValid('programing_deadline', project?.programingDeadline !== 'Invalid date' ? project?.programingDeadline : null);
            appendIfValid('is_schematic_design_enable', project?.isSchematicDesign);
            appendIfValid('schematic_design_deadline', project?.schematicDesignDeadline !== 'Invalid date' ? project?.schematicDesignDeadline : null);
            appendIfValid('is_design_development_enable', project?.isDesignDevelopment);
            appendIfValid('design_development_deadline',project?.designDevelopmentDeadline !== 'Invalid date' ? project?.designDevelopmentDeadline : null);
            appendIfValid('is_construction_document_enable', project?.isConstructionDocument);
            appendIfValid('construction_document_deadline', project?.constructionDocumentDeadline !== 'Invalid date' ? project?.constructionDocumentDeadline : null);
            appendIfValid('is_contract_admin', project?.isContractAdmin);
            appendIfValid('contract_admin_deadline', project?.contractAdminDeadline !== 'Invalid date' ? project?.contractAdminDeadline : null);
            appendIfValid('funding_source', project?.fundingSource);
            appendIfValid('total_construction_cost', Number(project?.totalCost));
            appendIfValid('total_approved_budget', Number(project?.totalBudget));
            appendIfValid('project_area', project?.projectArea);
            appendIfValid('currency', project?.currency);
            appendIfValid('default_project_units', project?.projectUnit);
            appendIfValid('is_tax_exempt', project?.isTax);
            appendIfValid('tax_rate', Number(project?.taxRate));
            appendIfValid('tax_number', !project?.isTax ? Number(project?.taxNo) : 0);
            appendIfValid('taxable_option', JSON.stringify(project?.taxableOption));
            appendIfValid('is_lock', project?.isProjectLock);
            appendIfValid('owner_id', project?.owner);
            appendIfValid('address_line_1', project?.address1);
            appendIfValid('address_line_2', project?.address2);
            appendIfValid('city', project?.city);
            appendIfValid('state', project?.state);
            appendIfValid('country', project?.country);
            appendIfValid('pincode', project?.zipcode);
            appendIfValid('created_by', user?.user_id);

            if (logo !== null) {
                if (logo.length > 0) {
                    formData.append('project_client_logo', JSON.stringify(logo));
                } else {
                    formData.append('project_client_logo', logo);
                }
            } else {
                formData.append('client_logo', "");
            }
            formData.append('notes', project?.isNotes ? project?.notes : '');
            try {
                setDataLoading(true);
                let response;
                if (projectId) {
                    const address = {
                        userId: user?.user_id,
                        organizationId: user?.organization?.organization_id,
                        addressId: addressId,
                        country: project?.country,
                        address1: project?.address1,
                        address2: project?.address2,
                        city: project?.city,
                        state: project?.state,
                        zipCode: project?.zipcode
                    }
                    if (address.addressId) {
                        dispatch(updateAddress(address));
                        formData.append('address_id', address.addressId);
                    } else {
                        const response = await apiRequest('post', `/address`, {
                            address_line_1: address?.address1,
                            address_line_2: address?.address2,
                            city: address?.city,
                            state: address?.state,
                            country: address?.country,
                            user_id: address?.userId,
                            organization_id: address?.organizationId,
                            pincode: address?.zipCode,
                            type: 'project',
                            project_id: projectId
                        });
                        if (response.data) {
                            formData.append('address_id', response.data.data.address_id);
                        }
                    }
                    response = await apiRequest('put', `/project/${projectId}`, formData);
                    if (response?.data) {
                        showNotification(response?.data?.message, 'success');
                        handleClose();
                        setLogo(null);
                        setDataLoading(false);
                    }
                } else {
                    response = await apiRequest('post', '/project', formData);
                    if (response?.data) {
                        showNotification(response?.data?.message, 'success');
                        navigate('/app/projects');
                        setLogo(null);
                        setDataLoading(false);
                    }
                }
            } catch (error) {
                setDataLoading(false)
                showNotification(error?.response?.data.error || error.message, 'error');
            }
        }
    }

    const getData = async () => {
        try {
            const response = await apiRequest('get', `/project/${projectId}`);
            if (response?.data) {
                const { data } = response?.data;
                setProject({
                    name: data?.name,
                    projectNo: data?.project_no,
                    clientOrg: data?.client_organization,
                    owner: data?.owner_id,
                    targetOccupancy: data?.target_occupation,
                    status: data?.status,
                    type: data?.type,
                    isNotes: data?.notes ? true : false,
                    isProjectLock: data?.is_lock,
                    notes: data?.notes,
                    fundingSource: data?.funding_source,
                    totalCost: data?.total_construction_cost,
                    totalBudget: data?.total_approved_budget,
                    projectArea: data?.project_area,
                    currency: data?.currency,
                    projectUnit: data?.default_project_units,
                    isTax: data?.is_tax_exempt,
                    taxNo: data?.tax_number,
                    taxRate: data?.tax_rate,
                    taxableOption: data?.taxable_option ? JSON.parse(data?.taxable_option) : [],
                    address1: data?.address?.address_line_1,
                    address2: data?.address?.address_line_2,
                    city: data?.address?.city,
                    state: data?.address?.state,
                    country: data?.address?.country,
                    zipcode: data?.address?.pincode,
                    isCertificateNeed: data?.is_certificate_need,
                    certificateDeadline: moment(data?.certificate_deadline).format('YYYY-MM-DD'),
                    isProgramingEnable: data?.is_programing_enable,
                    programingDeadline: moment(data?.programing_deadline).format('YYYY-MM-DD'),
                    isSchematicDesign: data?.is_schematic_design_enable,
                    schematicDesignDeadline: moment(data?.schematic_design_deadline).format('YYYY-MM-DD'),
                    isDesignDevelopment: data?.is_design_development_enable,
                    designDevelopmentDeadline: moment(data?.design_development_deadline).format('YYYY-MM-DD'),
                    isConstructionDocument: data?.is_construction_document_enable,
                    constructionDocumentDeadline: moment(data?.construction_document_deadline).format('YYYY-MM-DD'),
                    isContractAdmin: data?.is_contract_admin,
                    contractAdminDeadline: moment(data?.contract_admin_deadline).format('YYYY-MM-DD'),
                });
                setAddressId(data?.address?.address_id);
                setLogo(data.client_logo);
                setDataLoading(false);
            }
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (projectId) {
            setDataLoading(true);
            getData();
        }
    }, [projectId]);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setLogo(file);
        }
    };

    return (
        <Box component="main" sx={{ flexGrow: 1, p: projectId ? 0 : 3 }}>
            {!projectId && (<DividerHeader />)}
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <Box style={{ paddingBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>{projectId ? 'Edit Project' : 'Create Project'}</Typography>
                <Box>
                    {!project?.isProjectLock ? (
                        <Tooltip title="Lock Project">
                            <IconButton
                                onClick={() =>
                                    setProject((prevState) => ({
                                        ...prevState,
                                        isProjectLock: true
                                    }))}>
                                <LockOpenIcon />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="UnLock Project">
                            <IconButton onClick={() =>
                                setProject((prevState) => ({
                                    ...prevState,
                                    isProjectLock: false
                                }))}>
                                <LockIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Button variant="contained" color="primary" size="small" onClick={() => handleSubmit()}>
                        Save
                    </Button>
                    <Tooltip title="Cancel">
                        <IconButton onClick={() => {
                            if (projectId) {
                                handleClose();
                            } else {
                                navigate('/app/projects');
                                localStorage.removeItem('projectData');
                            }
                        }}>
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Divider />
            <div style={{ display: 'flex' }}>
                <Box
                    sx={{
                        height: '100%',
                        position: 'sticky',
                        top: 0,
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>Quick Navigation</Typography>
                    <Tabs
                        orientation="vertical"
                        value={selectedTab}
                        onChange={handleTabChange}
                        aria-label="Vertical tabs example"
                        sx={{
                            borderRight: 1,
                            borderColor: 'divider',
                            minWidth: 110,  // Adjust the width of tabs
                        }}
                    >
                        <Tab label="Project Details" sx={{ fontSize: 10, py: 0.1, minHeight: 20, padding: 0, alignItems: 'flex-start' }} />
                        <Tab label="Current Phase" sx={{ fontSize: 10, py: 0.1, minHeight: 20, padding: 0, alignItems: 'flex-start' }} />
                        <Tab label="Cost Details" sx={{ fontSize: 10, py: 0.1, minHeight: 20, padding: 0, alignItems: 'flex-start' }} />
                        <Tab label="Tax Details" sx={{ fontSize: 10, py: 0.1, minHeight: 20, padding: 0, alignItems: 'flex-start' }} />
                        <Tab label="Address Details" sx={{ fontSize: 10, py: 0.1, minHeight: 20, padding: 0, alignItems: 'flex-start' }} />
                    </Tabs>
                </Box>
                <Box
                    ref={contentRef}
                    sx={{
                        flexGrow: 1,
                        paddingLeft: 3,
                        paddingRight: 3,
                        overflowY: 'hidden',
                        height: '77vh'
                    }}>
                    <section id="section1" style={{ paddingTop: 10 }}>
                        <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 5 }}>Configure the details of this project</Typography>
                        <Grid container spacing={2} style={{ marginBottom: 10 }}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Project Name *</InputLabel>
                                    <OutlinedInput
                                        label="Project Name *"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.name || ''}
                                        onChange={(e) => {
                                            const newErrors = errors;
                                            if (e.target.value) {
                                                newErrors.name = '';
                                            } else {
                                                newErrors.name = 'Project Name is required'
                                            }
                                            setErrors(newErrors);
                                            setProject((prevState) => ({
                                                ...prevState,
                                                name: e.target.value
                                            }));
                                        }}
                                    />
                                    <Typography variant='body2' style={{ color: 'red' }}>{errors.name}</Typography>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Project ID *</InputLabel>
                                    <OutlinedInput
                                        label="Project ID *"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.projectNo || ''}
                                        onChange={(e) => {
                                            const newErrors = errors;
                                            if (e.target.value) {
                                                newErrors.projectNo = '';
                                            } else {
                                                newErrors.projectNo = 'Project ID is required'
                                            }
                                            setErrors(newErrors);
                                            setProject((prevState) => ({
                                                ...prevState,
                                                projectNo: e.target.value
                                            }));
                                        }}
                                    />
                                </FormControl>
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.projectNo}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Client Organization</InputLabel>
                                    <OutlinedInput
                                        label="Client Organization"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.clientOrg || ''}
                                        onChange={(e) => {
                                            setProject((prevState) => ({
                                                ...prevState,
                                                clientOrg: e.target.value
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={users.find((c) => c.user_id === project?.owner) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    options={users}
                                    getOptionLabel={(option) => `${option.first_name} ${option.last_name ? option.last_name : ''} (${option.email})`}
                                    onChange={(event, newValue) => {
                                        const newErrors = errors;
                                        if (newValue?.user_id) {
                                            newErrors.owner = '';
                                        } else {
                                            newErrors.owner = 'Please select project owner'
                                        }
                                        setErrors(newErrors);
                                        setProject((prevState) => ({
                                            ...prevState,
                                            owner: newValue?.user_id || null
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Project Owner *" name="Project Owner *" variant="outlined" />}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.owner}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password" shrink={project?.targetOccupancy === null || project?.targetOccupancy === '' || project?.targetOccupancy}>Target Occupancy *</InputLabel>
                                    <OutlinedInput
                                        label="Target Occupancy"
                                        id="standard-adornment-password"
                                        type='date'
                                        value={project?.targetOccupancy || ''}
                                        onChange={(e) => {
                                            const newErrors = errors;
                                            if (e.target.value) {
                                                newErrors.targetOccupancy = '';
                                            } else {
                                                newErrors.targetOccupancy = 'Please select Target Occupancy'
                                            }
                                            setErrors(newErrors);
                                            setProject((prevState) => ({
                                                ...prevState,
                                                targetOccupancy: e.target.value
                                            }))
                                        }}
                                    />
                                </FormControl>
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.targetOccupancy}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={projectStatus.find((c) => c.value === project?.status) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    options={projectStatus}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) => {
                                        const newErrors = errors;
                                        if (newValue?.value) {
                                            newErrors.status = '';
                                        } else {
                                            newErrors.status = 'Please select status of project'
                                        }
                                        setErrors(newErrors);
                                        setProject((prevState) => ({
                                            ...prevState,
                                            status: newValue?.value || null
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Project Status *" name="Project Status *" variant="outlined" />}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.status}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={projectType.find((c) => c.value === project?.type) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    options={projectType}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) => {
                                        const newErrors = errors;
                                        if (newValue?.value) {
                                            newErrors.type = '';
                                        } else {
                                            newErrors.type = 'Please select project type'
                                        }
                                        setErrors(newErrors);
                                        setProject((prevState) => ({
                                            ...prevState,
                                            type: newValue?.value || null
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Project Type *" name="Project Type *" variant="outlined" />}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.type}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Organization</InputLabel>
                                    <OutlinedInput
                                        label="Organization"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={user?.organization?.org_name || ''}
                                        disabled
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={6}>
                                <Box style={{ display: 'flex' }}>
                                    <FormGroup>
                                        <FormControlLabel control={<Switch checked={project?.isNotes} onChange={(e) => setProject((prevState) => ({
                                            ...prevState,
                                            isNotes: e.target.checked
                                        }))} />} label="Notes" />
                                    </FormGroup>
                                    {project?.isNotes && (
                                        <FormControl size="small" fullWidth variant="outlined">
                                            <InputLabel htmlFor="standard-adornment-password">Notes</InputLabel>
                                            <OutlinedInput
                                                label="Notes"
                                                id="standard-adornment-password"
                                                type='text'
                                                multiline
                                                minRows={3}
                                                maxRows={3}
                                                value={project?.notes || ''}
                                                onChange={(e) => setProject((prevState) => ({
                                                    ...prevState,
                                                    notes: e.target.value
                                                }))}
                                            />
                                        </FormControl>)}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={6}>
                                <Box style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    <Tooltip title="upload Client logo">
                                        <IconButton component="label" style={{ padding: 0, width: 40, height: 40, border: '1px solid black' }}>
                                            <CloudUploadIcon />
                                            <VisuallyHiddenInput
                                                type="file"
                                                onChange={(event) => handleFileUpload(event)}
                                                accept=".jpeg,.jpg,.png,.PNG"
                                            />
                                        </IconButton>
                                    </Tooltip>
                                    {logo?.length > 0 ? (
                                        logo.map((a) => (
                                            <Box style={{ position: 'relative', padding: '7px 30px 0px 30px', border: '1px solid lightgray' }}>
                                                <img src={`${process.env.REACT_APP_IMAGEURL}bbn=${a?.sortUrl}&uid=${user?.user_id}`} alt='logo' style={{ width: '50px', height: '50px' }} />
                                                <Tooltip title="remove logo">
                                                    <ClearIcon
                                                        style={{
                                                            position: 'absolute',
                                                            right: 1,
                                                            top: 1,
                                                            fontSize: 20,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={async () => {
                                                            setLogo(null);
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        ))
                                    ) : logo !== null && (
                                        <Typography color='primary'>{logo?.name}</Typography>
                                    )}

                                </Box>
                            </Grid>
                        </Grid>
                    </section>
                    <Divider />
                    <section id="section2" style={{ paddingTop: 10 }}>
                        <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 5 }}>Select the current phase of the project, and the deadline by which is needs to be completed</Typography>
                        <Grid container spacing={2} style={{ marginBottom: 10 }}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Typography variant="body2">Certificate of need/initial budget</Typography>
                                <Box style={{ display: 'flex' }}>
                                    <Switch checked={project?.isCertificateNeed} onChange={(e) => setProject((prevState) => ({
                                        ...prevState,
                                        isCertificateNeed: e.target.checked,
                                        isProgramingEnable: false,
                                        isSchematicDesign: false,
                                        isDesignDevelopment: false,
                                        isConstructionDocument: false,
                                        isContractAdmin: false,
                                    }))} />
                                    <FormControl size="small" fullWidth variant="outlined">
                                        <OutlinedInput
                                            id="standard-adornment-password"
                                            type='date'
                                            value={project?.certificateDeadline}
                                            onChange={(e) => setProject((prevState) => ({
                                                ...prevState,
                                                certificateDeadline: e.target.value
                                            }))}
                                            disabled={!project?.isCertificateNeed}
                                        />
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Typography variant="body2">Programming</Typography>
                                <Box style={{ display: 'flex' }}>
                                    <Switch checked={project?.isProgramingEnable} onChange={(e) => setProject((prevState) => ({
                                        ...prevState,
                                        isCertificateNeed: false,
                                        isProgramingEnable: e.target.checked,
                                        isSchematicDesign: false,
                                        isDesignDevelopment: false,
                                        isConstructionDocument: false,
                                        isContractAdmin: false,
                                    }))} />
                                    <FormControl size="small" fullWidth variant="outlined">
                                        <OutlinedInput
                                            id="standard-adornment-password"
                                            type='date'
                                            value={project?.programingDeadline}
                                            onChange={(e) => setProject((prevState) => ({
                                                ...prevState,
                                                programingDeadline: e.target.value
                                            }))}
                                            disabled={!project?.isProgramingEnable}
                                        />
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Typography variant="body2">Schematic Design (SD)</Typography>
                                <Box style={{ display: 'flex' }}>
                                    <Switch checked={project?.isSchematicDesign} onChange={(e) => setProject((prevState) => ({
                                        ...prevState,
                                        isCertificateNeed: false,
                                        isProgramingEnable: false,
                                        isSchematicDesign: e.target.checked,
                                        isDesignDevelopment: false,
                                        isConstructionDocument: false,
                                        isContractAdmin: false,
                                    }))} />
                                    <FormControl size="small" fullWidth variant="outlined">
                                        <OutlinedInput
                                            id="standard-adornment-password"
                                            type='date'
                                            value={project?.schematicDesignDeadline}
                                            onChange={(e) => setProject((prevState) => ({
                                                ...prevState,
                                                schematicDesignDeadline: e.target.value
                                            }))}
                                            disabled={!project?.isSchematicDesign}
                                        />
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Typography variant="body2">Design Development (DD)</Typography>
                                <Box style={{ display: 'flex' }}>
                                    <Switch checked={project?.isDesignDevelopment} onChange={(e) => setProject((prevState) => ({
                                        ...prevState,
                                        isCertificateNeed: false,
                                        isProgramingEnable: false,
                                        isSchematicDesign: false,
                                        isDesignDevelopment: e.target.checked,
                                        isConstructionDocument: false,
                                        isContractAdmin: false,
                                    }))} />
                                    <FormControl size="small" fullWidth variant="outlined">
                                        <OutlinedInput
                                            id="standard-adornment-password"
                                            type='date'
                                            value={project?.designDevelopmentDeadline}
                                            onChange={(e) => setProject((prevState) => ({
                                                ...prevState,
                                                designDevelopmentDeadline: e.target.value
                                            }))}
                                            disabled={!project?.isDesignDevelopment}
                                        />
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Typography variant="body2">Construction Document (CD)</Typography>
                                <Box style={{ display: 'flex' }}>
                                    <Switch checked={project?.isConstructionDocument} onChange={(e) => setProject((prevState) => ({
                                        ...prevState,
                                        isCertificateNeed: false,
                                        isProgramingEnable: false,
                                        isSchematicDesign: false,
                                        isDesignDevelopment: false,
                                        isConstructionDocument: e.target.checked,
                                        isContractAdmin: false,
                                    }))} />
                                    <FormControl size="small" fullWidth variant="outlined">
                                        <OutlinedInput
                                            id="standard-adornment-password"
                                            type='date'
                                            value={project?.constructionDocumentDeadline}
                                            onChange={(e) => setProject((prevState) => ({
                                                ...prevState,
                                                constructionDocumentDeadline: e.target.value
                                            }))}
                                            disabled={!project?.isConstructionDocument}
                                        />
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Typography variant="body2">Contract Administration</Typography>
                                <Box style={{ display: 'flex' }}>
                                    <Switch checked={project?.isContractAdmin} onChange={(e) => setProject((prevState) => ({
                                        ...prevState,
                                        isCertificateNeed: false,
                                        isProgramingEnable: false,
                                        isSchematicDesign: false,
                                        isDesignDevelopment: false,
                                        isConstructionDocument: false,
                                        isContractAdmin: e.target.checked,
                                    }))} />
                                    <FormControl size="small" fullWidth variant="outlined">
                                        <OutlinedInput
                                            id="standard-adornment-password"
                                            type='date'
                                            value={project?.contractAdminDeadline}
                                            onChange={(e) => setProject((prevState) => ({
                                                ...prevState,
                                                contractAdminDeadline: e.target.value
                                            }))}
                                            disabled={!project?.isContractAdmin}
                                        />
                                    </FormControl>
                                </Box>
                            </Grid>
                        </Grid>
                    </section>
                    <Divider />
                    <section id="section3" style={{ paddingTop: 10 }}>
                        <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 5 }}>Record important financial data for the project</Typography>
                        <Grid container spacing={2} style={{ marginBottom: 10 }}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={FundingSource.find((c) => c.value === project?.fundingSource) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    options={FundingSource}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) => {
                                        const newErrors = errors;
                                        if (newValue?.value) {
                                            newErrors.fundingSource = '';
                                        } else {
                                            newErrors.fundingSource = 'Please select source of funding'
                                        }
                                        setErrors(newErrors);
                                        setProject((prevState) => ({
                                            ...prevState,
                                            fundingSource: newValue?.value || null
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Funding Source *" name="Funding Source *" variant="outlined" />}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.fundingSource}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Total Construction Cost</InputLabel>
                                    <OutlinedInput
                                        label="Total Construction Cost"
                                        id="standard-adornment-password"
                                        type='number'
                                        value={project?.totalCost || ''}
                                        onChange={(e) => {
                                            setProject((prevState) => ({
                                                ...prevState,
                                                totalCost: e.target.value
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Total Approved Budget</InputLabel>
                                    <OutlinedInput
                                        label="Total Approved Budget"
                                        id="standard-adornment-password"
                                        type='number'
                                        value={project?.totalBudget || ''}
                                        onChange={(e) => {
                                            setProject((prevState) => ({
                                                ...prevState,
                                                totalBudget: e.target.value
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">{`Project Area (${project.projectUnit === 'matric' ? 'sq.m' : 'sq.ft'})`}</InputLabel>
                                    <OutlinedInput
                                        label={`Project Area (${project.projectUnit === 'matric' ? 'sq.m' : 'sq.ft'})`}
                                        id="standard-adornment-password"
                                        type='number'
                                        value={project?.projectArea || ''}
                                        onChange={(e) => {
                                            setProject((prevState) => ({
                                                ...prevState,
                                                projectArea: e.target.value
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={CurrencyJSON.find((c) => c.value === project?.currency) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    clearIcon={null}
                                    options={CurrencyJSON}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) => {
                                        setProject((prevState) => ({
                                            ...prevState,
                                            currency: newValue?.value || CurrencyJSON[0]?.value
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Currency" name="Currency" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={ProjectUnit.find((c) => c.value === project?.projectUnit) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    options={ProjectUnit}
                                    clearIcon={null}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) => {
                                        setProject((prevState) => ({
                                            ...prevState,
                                            projectUnit: newValue?.value || ProjectUnit[0]?.value
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Default Project Unit" name="Default Project Unit" variant="outlined" />}
                                />
                            </Grid>
                        </Grid>
                    </section>
                    <Divider />
                    <section id="section4" style={{ paddingTop: 10 }}>
                        <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 5 }}>Indicate the tax-exempt status of the project, and which option are taxable.</Typography>
                        <Grid container style={{ marginBottom: 10 }}>
                            <Grid item xs={12} sm={12} md={12} lg={12}>
                                <FormGroup>
                                    <FormControlLabel control={<Switch checked={project.isTax} onClick={(e) => {
                                        setProject((prevState) => ({
                                            ...prevState,
                                            isTax: e.target.checked,
                                            taxNo: null,
                                        }));
                                    }} />} label="This project is tax-exempt" />
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12}>
                                <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
                                    {!project.isTax && (
                                        <>
                                            <Grid item xs={12} sm={12} md={6} lg={3}>
                                                <FormControl size="small" fullWidth variant="outlined">
                                                    <InputLabel htmlFor="standard-adornment-password">Tax Rate(xx.xx%)</InputLabel>
                                                    <OutlinedInput
                                                        label="Tax Rate(xx.xx%)"
                                                        id="standard-adornment-password"
                                                        type='number'
                                                        value={project?.taxRate || ''}
                                                        onChange={(e) => {
                                                            setProject((prevState) => ({
                                                                ...prevState,
                                                                taxRate: e.target.value,
                                                            }));
                                                        }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={6} lg={3}>
                                                <FormControl size="small" fullWidth variant="outlined">
                                                    <InputLabel htmlFor="standard-adornment-password">Tax ID Number</InputLabel>
                                                    <OutlinedInput
                                                        label="Tax ID Number"
                                                        id="standard-adornment-password"
                                                        type='number'
                                                        value={project?.taxNo || ''}
                                                        onChange={(e) => {
                                                            setProject((prevState) => ({
                                                                ...prevState,
                                                                taxNo: e.target.value,
                                                            }));
                                                        }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </>)}
                                    <Grid item xs={12} sm={12} md={6} lg={6}>
                                        <InputLabel htmlFor="standard-adornment-password">Taxable Options</InputLabel>
                                        <FormControl size="small" fullWidth variant="standard">
                                            <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
                                                {TaxableOption.map((a) => (
                                                    <FormControlLabel
                                                        key={a.value}
                                                        value={a.value}
                                                        control={
                                                            <Checkbox
                                                                checked={project.taxableOption.includes(a.value)}
                                                                onChange={(e) => {
                                                                    const arr = [...project.taxableOption];
                                                                    const index = arr?.findIndex((a) => a === e.target.value);
                                                                    if (index > -1) {
                                                                        arr.splice(index, 1);
                                                                    } else {
                                                                        arr.push(e.target.value);
                                                                    }
                                                                    setProject((prevState) => ({
                                                                        ...prevState,
                                                                        taxableOption: arr
                                                                    }));
                                                                }}
                                                                value={a.value}
                                                            />
                                                        }
                                                        label={a.name}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </section>
                    <Divider />
                    <section id="section5" style={{ paddingTop: 10 }}>
                        <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: 5 }}>Project Address</Typography>
                        <Grid container spacing={2} style={{ marginBottom: 10 }}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Address 1 *</InputLabel>
                                    <OutlinedInput
                                        label="Address 1"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.address1 || ''}
                                        onChange={(e) => {
                                            const newErrors = errors;
                                            if (e.target.value) {
                                                newErrors.address1 = ''
                                            } else {
                                                newErrors.address1 = 'Address1 is required'
                                            }
                                            setErrors(newErrors);
                                            setProject((prevState) => ({
                                                ...prevState,
                                                address1: e.target.value || null
                                            }));
                                        }}
                                    />
                                    <Typography variant='body2' style={{ color: 'red' }}>{errors.address1}</Typography>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Address 2</InputLabel>
                                    <OutlinedInput
                                        label="Address 2"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.address2 || ''}
                                        onChange={(e) => {
                                            setProject((prevState) => ({
                                                ...prevState,
                                                address2: e.target.value || null
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">City *</InputLabel>
                                    <OutlinedInput
                                        label="City"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.city || ''}
                                        onChange={(e) => {
                                            const newErrors = errors;
                                            if (e.target.value) {
                                                newErrors.city = ''
                                            } else {
                                                newErrors.city = 'City is required'
                                            }
                                            setErrors(newErrors);
                                            setProject((prevState) => ({
                                                ...prevState,
                                                city: e.target.value || null
                                            }));
                                        }}
                                    />
                                    <Typography variant='body2' style={{ color: 'red' }}>{errors.city}</Typography>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">State</InputLabel>
                                    <OutlinedInput
                                        label="State"
                                        id="standard-adornment-password"
                                        type='text'
                                        value={project?.state || ''}
                                        onChange={(e) => {
                                            setProject((prevState) => ({
                                                ...prevState,
                                                state: e.target.value || null
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    value={countries.find((c) => c.code.toLowerCase() === project?.country) || null}
                                    id="combo-box-demo"
                                    size="small"
                                    fullWidth
                                    options={countries}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) => {
                                        const newErrors = errors;
                                        if (newValue?.code) {
                                            newErrors.country = ''
                                        } else {
                                            newErrors.country = 'Please select a country'
                                        }
                                        setErrors(newErrors);
                                        setProject((prevState) => ({
                                            ...prevState,
                                            country: newValue?.code.toLowerCase() || null
                                        }));
                                    }}
                                    renderInput={(params) => <TextField fullWidth {...params} label="Country *" name="Country *" variant="outlined" />}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.country}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Zip Code *</InputLabel>
                                    <OutlinedInput
                                        label="Zip Code"
                                        id="standard-adornment-password"
                                        type='number'
                                        value={project?.zipcode || ''}
                                        onChange={(e) => {
                                            const newErrors = errors;
                                            if (e.target.value) {
                                                newErrors.zipcode = ''
                                            } else {
                                                newErrors.zipcode = 'Zip Code is required'
                                            }
                                            setErrors(newErrors);
                                            setProject((prevState) => ({
                                                ...prevState,
                                                zipcode: e.target.value || null
                                            }));
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.keyCode === 38 || event.keyCode === 40) {
                                                event.preventDefault();
                                            }
                                        }}
                                    />
                                    <Typography variant='body2' style={{ color: 'red' }}>{errors.zipcode}</Typography>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </section>
                    <Divider />
                </Box>
            </div>
        </Box>
    )
}